import { isEmpty, isNil } from 'lodash';
import { CSSProperties, useCallback, useContext } from 'react';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { InputBlockMedia } from '@shared/components/blocks/lib/inputFile/InputBlockMedia';
import { useFileUpload } from '@shared/components/blocks/lib/inputFile/hooks';
import { FileUpload } from '@shared/components/blocks/types';
import { AuditTagField } from '@shared/data/entity/fields/AuditTagField';
import { ColorThemeContext, getAnchorName } from '@shared/styles';
import { API } from '@shared/types/api';
import { useTrigger } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { AuditFieldFooterActions } from '../AuditFieldFooterActions';
import { EditFieldContainer } from '../EditFieldContainer';
import { AuditTagList } from './AuditTagList';
import { AuditTagModal } from './AuditTagModal';

interface Props {
  field: AuditTagField | null;
  component: API.AuditTagComponent;
  updateField?: (componentId: string, field: AuditTagField | null) => void;
  error?: string;
  showIcon?: boolean;
  style?: CSSProperties;
}

export function AuditTagEditField({
  field,
  component,
  updateField,
  error,
  showIcon,
  style,
}: Props) {
  const colors = useContext(ColorThemeContext);
  const [answerTrigger, onAnswerTrigger] = useTrigger();
  const anchorName = getAnchorName('audit-tag-edit-field', component.id);
  const isReadOnly = component.readonly || isNil(updateField);

  const onTagsChange = useCallback(
    (selectedTags: string[]) => {
      updateField?.(
        component.id,
        !isEmpty(selectedTags)
          ? new AuditTagField({
              tags: selectedTags,
              files: field?.files ?? [],
              notes: field?.notes ?? '',
            })
          : null,
      );
    },
    [component.id, updateField, field],
  );

  const onNotesChange = useCallback(
    (updatedNotes: string) => {
      updateField?.(
        component.id,
        new AuditTagField({
          tags: field?.tags ?? [],
          files: field?.files ?? [],
          notes: updatedNotes,
        }),
      );
    },
    [component.id, updateField, field],
  );

  const onFileAdded = useCallback(
    (file: FileUpload) => {
      updateField?.(
        component.id,
        new AuditTagField({
          tags: field?.tags ?? [],
          files: [...(field?.files ?? []), file],
          notes: field?.notes ?? '',
        }),
      );
    },
    [component.id, updateField, field],
  );

  const onFileRemoved = useCallback(
    (file: FileUpload) => {
      updateField?.(
        component.id,
        new AuditTagField({
          tags: field?.tags ?? [],
          files: (field?.files ?? []).filter(f => f.url !== file.url),
          notes: field?.notes ?? '',
        }),
      );
    },
    [component.id, updateField, field],
  );

  const { showFilePicker, uploading } = useFileUpload({ onFileAdded, onFileRemoved });

  const tagOptions = component.options?.filter(option => !option.archived) ?? [];
  const hasTags = !isEmpty(field?.tags ?? []);

  return (
    <EditFieldContainer
      component={component}
      anchorName={anchorName}
      showIcon={showIcon ?? false}
      showLabel={false}
      showPlaceholder={false}
      onPress={isReadOnly ? undefined : onAnswerTrigger}
      error={error}
      style={style}
      notes={
        !!field?.notes && (
          <Text size="body2" color={colors.foregroundActive}>
            {field.notes}
          </Text>
        )
      }
      files={
        (field?.files ?? []).length > 0 && (
          <InputBlockMedia
            files={field?.files ?? []}
            maxWidth={200}
            maxHeight={133}
            minWidth={100}
            compactFileView={false}
            editable={!isReadOnly}
            onRemoved={onFileRemoved}
          />
        )
      }
      footer={
        !isReadOnly && (
          <AuditFieldFooterActions
            showFilePicker={showFilePicker}
            uploading={uploading}
            notes={field?.notes ?? ''}
            onNotesChange={onNotesChange}
            onAnswerPress={onAnswerTrigger}
          />
        )
      }
    >
      <AuditTagModal
        anchorName={anchorName}
        onChange={onTagsChange}
        trigger={answerTrigger}
        label={i18n('card.blocks.question.answerButtonLabel')}
        isMultiselect={component.multiselect ?? false}
        tagOptions={tagOptions}
        tags={field?.tags ?? []}
        isReadOnly={isReadOnly}
      >
        <View paddingHorizontal={2} paddingVertical={1}>
          <Text size="body1" color={colors.foregroundActive}>
            {component.label}
          </Text>
        </View>
        {hasTags && (
          <View
            row
            wrap
            shrinkWrap
            style={{ maxWidth: '100%' }}
            paddingHorizontal={1}
            paddingBottom={1}
          >
            <AuditTagList tagOptions={component.options} tags={field?.tags ?? []} />
          </View>
        )}
      </AuditTagModal>
    </EditFieldContainer>
  );
}
